import {
  useCurrentUserContext,
  useCreateSmallDocumentWithMetadata,
  useUpdateSmallDocumentWithMetadata
} from '@cs-cms/state-management';
import {
  CpsDocumentMetadata,
  CreateSmallDocumentWithMetadataRequest,
  UpdateSmallDocumentWithMetadataRequest
} from '@cs-cms/types';

interface UseSmallFileUploadParams {
  isNewDocument: boolean;
}

interface SmallFileCreateParams {
  file: File;
  ctName: string;
  metadata: CpsDocumentMetadata;
  retry?: boolean;
}

interface SmallFileUpdateParams {
  file: File;
  ctName: string;
  documentId: string;
  metadata: CpsDocumentMetadata;
  etag?: string;
  retry?: boolean;
}

interface SmallFileUploadOptions {
  onSuccess?: () => void;
}

export const useSmallFileUpload = ({ isNewDocument }: UseSmallFileUploadParams) => {
  const { selectedIndex } = useCurrentUserContext();
  const { mutate: mutateCreateSmallDocumentWithMetadata, isPending: isCreating } = useCreateSmallDocumentWithMetadata();
  const { mutate: mutateUpdateSmallDocumentWithMetadata, isPending: isUpdating } = useUpdateSmallDocumentWithMetadata();

  const createSmallFile = (params: SmallFileCreateParams, options?: SmallFileUploadOptions) => {
    const dto: CreateSmallDocumentWithMetadataRequest = {
      ctName: params.ctName,
      indexId: selectedIndex?.id ?? -1,
      file: params.file,
      metadata: params.metadata
    };

    mutateCreateSmallDocumentWithMetadata({ dto, retry: params.retry }, { onSuccess: options?.onSuccess });
  };

  const updateSmallFile = (params: SmallFileUpdateParams, options?: SmallFileUploadOptions) => {
    const dto: UpdateSmallDocumentWithMetadataRequest = {
      ctName: params.ctName,
      documentId: params.documentId,
      indexId: selectedIndex?.id ?? -1,
      file: params.file,
      metadata: params.metadata
    };

    mutateUpdateSmallDocumentWithMetadata(
      { dto, etag: params.etag, retry: params.retry },
      { onSuccess: options?.onSuccess }
    );
  };

  const uploadSmallFile = (params: SmallFileCreateParams | SmallFileUpdateParams, options?: SmallFileUploadOptions) => {
    if (isNewDocument) {
      createSmallFile(params as SmallFileCreateParams, options);
    } else {
      updateSmallFile(params as SmallFileUpdateParams, options);
    }
  };

  return {
    uploadSmallFile,
    createSmallFile,
    updateSmallFile,
    isLoading: isCreating || isUpdating
  };
};
